import React, { useState, useRef } from "react";
import { FaStar, FaTimes } from "react-icons/fa";
import { motion } from "framer-motion";
import GoogleReviews from "../components/GoogleReviews";
import ContactUsForm from "../components/ContactForm";

const CounsellingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const contactRef = useRef(null);

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  /* ================= DATA ================= */

  const packages = [
    {
      name: "Basic Package",
      price: "₹8000 + GST",
      description: "Counselling and Admission support.",
      color: "from-blue-400 to-blue-600",
    },
    {
      name: "Standard Package",
      price: "₹16000 + GST",
      description: "IELTS, Admission & Visa assistance.",
      mostPopular: true,
      color: "from-purple-400 to-purple-600",
      details: [
        "Initial FREE consultation",
        "IELTS preparation guidance",
        "University admission support",
        "Visa process support",
      ],
    },
    {
      name: "Premium Package",
      price: "₹24000 + GST",
      description:
        "Full personalized service + SOP/LOR & refusal handling.",
      color: "from-indigo-400 to-indigo-600",
      details: [
        "Initial FREE consultation",
        "Personalized IELTS support",
        "University admission counseling",
        "Visa support",
        "SOP & LOR guidance",
        "Refusal case handling",
      ],
    },
  ];

  const services = [
    {
      title: "Career Counselling",
      icon: "🎯",
      description:
        "Explore your career options and make informed decisions about your future.",
    },
    {
      title: "Education Counselling",
      icon: "🎓",
      description:
        "Get guidance on choosing the right educational path for your goals.",
    },
    {
      title: "Application Assistance",
      icon: "📝",
      description:
        "Expert help with your applications to increase chances of success.",
    },
    {
      title: "Visa Counselling",
      icon: "✈️",
      description:
        "Navigate the visa process with confidence and expert guidance.",
    },
    {
      title: "Financial Advice",
      icon: "💰",
      description:
        "Understanding funding options and financial planning for your education.",
    },
    {
      title: "Interview Preparation",
      icon: "🎤",
      description:
        "Comprehensive preparation for academic and visa interviews.",
    },
  ];

  const processSteps = [
    {
      emoji: "📞",
      title: "Initial Consultation",
      description:
        "Connect with our experts to discuss your goals and expectations.",
    },
    {
      emoji: "📝",
      title: "Profile Evaluation",
      description:
        "We assess your academic background and career aspirations.",
    },
    {
      emoji: "🏫",
      title: "University Shortlisting",
      description:
        "We help you shortlist the best-fit universities for your profile.",
    },
    {
      emoji: "✍️",
      title: "Application Assistance",
      description:
        "From SOPs to LORs, we guide you through every document step.",
    },
    {
      emoji: "🎓",
      title: "Final Admission & Visa",
      description:
        "Get your offer letter and complete your visa formalities confidently.",
    },
  ];

  const handleCardClick = (pkg) => {
    setSelectedPackage(pkg);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPackage(null);
  };

  /* ================= UI ================= */

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">

      {/* ================= HERO ================= */}
      <section className="relative bg-gradient-to-br from-blue-100 to-indigo-100 py-28 px-6 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-purple-300 rounded-full blur-3xl opacity-60 animate-blob" />
          <div className="absolute top-0 -right-24 w-96 h-96 bg-yellow-300 rounded-full blur-3xl opacity-60 animate-blob animation-delay-2000" />
          <div className="absolute -bottom-24 left-20 w-96 h-96 bg-pink-300 rounded-full blur-3xl opacity-60 animate-blob animation-delay-4000" />
        </div>

        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center px-6 py-10 rounded-3xl bg-white/40 backdrop-blur-xl shadow-xl"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 text-gray-900">
              Transform Your Future <br className="hidden sm:block" />
              <span className="text-indigo-700">with Expert Guidance</span>
            </h1>

            <p className="text-lg sm:text-xl text-gray-700 mb-10 max-w-2xl mx-auto">
              Get personalised counselling to achieve your academic and career
              goals — from course selection to visa approval.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={scrollToContact}
                className="bg-indigo-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-indigo-700 transition"
              >
                Book Free Consultation
              </button>

              <button
                onClick={() =>
                  window.open("https://wa.me/919825772794", "_blank")
                }
                className="border border-indigo-600 text-indigo-600 px-8 py-4 rounded-xl font-semibold hover:bg-indigo-50 transition"
              >
                Chat on WhatsApp
              </button>
            </div>

            <p className="text-sm text-gray-600 mt-6">
              ✔ First session is{" "}
              <span className="font-semibold text-green-600">100% FREE</span>
              &nbsp;•&nbsp; ✔ Certified counsellors &nbsp;•&nbsp; ✔ No obligation
            </p>
          </motion.div>
        </div>
      </section>

      {/* ================= SERVICES ================= */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              End-to-end guidance designed to simplify your study abroad journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((s, i) => (
              <div
                key={i}
                className="relative bg-white rounded-2xl border border-gray-100 p-8 shadow-md hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <span className="absolute top-0 left-0 w-full h-1 bg-indigo-500 rounded-t-2xl" />
                <div className="text-3xl mb-4">{s.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
                <p className="text-gray-600 text-sm">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PROCESS ================= */}
<section className="relative bg-white py-24 px-4">
  <div className="max-w-6xl mx-auto">

    {/* Heading */}
    <div className="text-center mb-20">
      <h2 className="text-4xl font-bold mb-4 text-gray-900">
        Our Counselling Process
      </h2>
      <p className="text-gray-600 max-w-2xl mx-auto">
        A structured, expert-driven journey that takes you from confusion to
        confident admission and visa approval.
      </p>
    </div>

    {/* Timeline Wrapper */}
    <div className="relative">

      {/* Connecting Line (Desktop only) */}
      <div className="hidden md:block absolute top-1/2 left-0 right-0 h-[2px] bg-gradient-to-r from-indigo-200 via-indigo-400 to-indigo-200"></div>

      {/* Cards */}
      <div className="flex gap-8 overflow-x-auto px-2 scrollbar-hide relative z-10">
        {processSteps.map((step, i) => (
          <div
            key={i}
            className="group relative min-w-[280px] max-w-xs bg-white rounded-2xl border border-gray-200 p-8 shadow-lg transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl hover:border-indigo-500"
          >
            {/* Step Number */}
            <div className="absolute -top-5 left-6 w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold shadow-md">
              {i + 1}
            </div>

            {/* Icon */}
            <div className="text-4xl mb-5 mt-4 transition-transform duration-300 group-hover:scale-110">
              {step.emoji}
            </div>

            {/* Title */}
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              {step.title}
            </h3>

            {/* Description */}
            <p className="text-gray-600 text-sm leading-relaxed">
              {step.description}
            </p>
          </div>
        ))}
      </div>
    </div>

    {/* Mobile Hint */}
    <p className="text-center text-sm text-gray-500 mt-8 md:hidden">
      Swipe horizontally to explore each step →
    </p>
  </div>
</section>

      {/* ================= PACKAGES ================= */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">Choose Your Package</h2>
          <p className="text-gray-600">
            Start with a <span className="text-green-600 font-semibold">FREE consultation</span> — upgrade anytime.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {packages.map((pkg, i) => (
            <div
              key={i}
              onClick={() => handleCardClick(pkg)}
              className={`relative cursor-pointer rounded-2xl p-8 text-white shadow-lg bg-gradient-to-br ${pkg.color} ${
                pkg.mostPopular ? "ring-4 ring-yellow-300/60 scale-105" : ""
              }`}
            >
              {pkg.mostPopular && (
                <div className="absolute -top-4 right-4 bg-yellow-400 text-black px-4 py-1 rounded-full text-sm flex items-center">
                  <FaStar className="mr-1" /> Most Popular
                </div>
              )}

              <div className="absolute top-4 left-4 bg-green-500 text-xs px-3 py-1 rounded-full">
                FREE Consultation
              </div>

              <h3 className="text-2xl font-bold mt-10">{pkg.name}</h3>
              <p className="text-3xl font-extrabold mt-3">
                {pkg.price}
                <span className="block text-xs font-normal text-white/80 mt-1">
                  One-time fee
                </span>
              </p>

              <p className="text-white/90 text-sm mt-4 mb-6">
                {pkg.description}
              </p>

              <button className="w-full bg-white/20 py-3 rounded-xl font-semibold hover:bg-white/30">
                View Details
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ================= REVIEWS ================= */}
      <section className="bg-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-12">Success Stories</h2>
          <GoogleReviews />
        </div>
      </section>

      {/* ================= MODAL ================= */}
      {isModalOpen && selectedPackage && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl p-8 max-w-md w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 text-gray-400"
              onClick={closeModal}
            >
              <FaTimes size={22} />
            </button>

            <h3 className="text-2xl font-bold mb-2">
              {selectedPackage.name}
            </h3>
            <div className="w-16 h-1 bg-blue-600 rounded-full mb-4" />

            <p className="text-3xl font-bold text-blue-600 mb-4">
              {selectedPackage.price}
            </p>

            <p className="text-gray-600 mb-6">
              {selectedPackage.description}
            </p>

            {selectedPackage.details && (
              <ul className="space-y-2 mb-6">
                {selectedPackage.details.map((d, i) => (
                  <li key={i} className="text-gray-600 text-sm">✔ {d}</li>
                ))}
              </ul>
            )}

            <button
              onClick={scrollToContact}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-xl font-semibold"
            >
              Proceed with Free Consultation
            </button>
          </div>
        </div>
      )}

      {/* ================= CONTACT ================= */}
      <div ref={contactRef} className="scroll-mt-24">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <h3 className="text-2xl font-bold mb-2">
            Book Your Free Counselling Session
          </h3>
          <p className="text-gray-600">
            Speak directly with an expert — no charges, no obligation.
          </p>
        </div>
        <ContactUsForm />
      </div>
    </div>
  );
};

export default CounsellingPage;
